import { Fragment, useState, type ReactNode } from 'react';
import { EDIT_FIELDS, companyBillingSitesPath, countiesPath, dollarsToCents, fieldDefault, fill, TAX_CATEGORY_DEFAULT_BY_ITEM_TYPE, normalizeEmail, normalizePostalCode, shownOptions, Size, t, titleCaseName, validateEmail, validatePostalCode, visibleEditFields, type EditField, type EditOptionSource, type PagedRecord } from '@bms2/shared';
import { Button, Checkbox, DatePicker, Dropdown, NumericInput, PhoneInput, Select, Textarea, TextInput } from '../../components/controls';
import { useCompanySearch } from '../settings/api';
import { PAGES_PATH, useApi } from './api';
import { NewRecordModal } from './NewRecordModal';
import { SitePicker, useSiteChoices, type SiteChoiceRow } from './SitePicker';
import { SiteTypeField } from './SiteTypeField';
import styles from './RecordPage.module.css';
import fieldStyles from '../../components/controls/field.module.css';

/**
 * A record's own field form (piece 13). 🔨 One form, rendered either as a full
 * page or nested inside the Quick-Create Modal wrapper — the piece says the
 * quick-create nests the New/Edit page's form, so this is that form and there
 * is not a second one to drift from it.
 *
 * The fields come from the shared register. A field absent there is not
 * editable from either wrapper: a custom field is written through its own tile,
 * a tag through the Flags tile, and a dated link is closed and opened.
 *
 * Piece 17 adds the closed lists an Item reads (figure 107): a select over a
 * reference read, Type as a radio, the manufacturer as a Company Dropdown
 * whose Add New quick-creates one, and the Goods-only block hidden for a
 * Service rather than greyed.
 */
export interface RecordFieldsProps {
  recordType: PagedRecord;
  /** What each field holds now: the draft where one was typed, the record's own otherwise. */
  read: (key: string) => unknown;
  onChange: (key: string, value: unknown) => void;
  apiBaseUrl?: string;
  /** A create draws every field; an edit leaves the create-only ones off, such as a Deal's origin (decision 0353). */
  mode?: 'create' | 'edit';
  /** The record the form edits, which a site field lists the sites of (decision 0669). */
  recordId?: string | null;
  /** Its name, which the + Add Site window a site field opens shows locked. */
  recordLabel?: string | null;
  /** The keys drawn, where the save writes fewer than the whole form: a change request's Accept by an account owner without Company write (T355). */
  only?: readonly string[] | null;
  /** Fields drawn after the record's own, where the opener adds what its create writes beside the record: a message's sender's company and address (decision 1506). */
  extra?: readonly EditField[];
}

export const ITEM_OPTIONS_PATH = `${PAGES_PATH}/item-options`;

type Options = Record<EditOptionSource, { value: string; label: string; hint?: string | null }[]>;

export function RecordFields({ recordType, read, onChange, apiBaseUrl = '', mode = 'edit', recordId = null, recordLabel = null, only = null, extra = [] }: RecordFieldsProps) {
  const needsOptions = EDIT_FIELDS[recordType].some((field) => field.optionsFrom && field.optionsFrom !== 'siteTypes');
  const options = useApi<Options>(needsOptions ? ITEM_OPTIONS_PATH : null, apiBaseUrl);
  // 🔒 Decision 10100: the site types come from their own route, the active ones in their order, never the Item form's options; the Site type field reads it (decision 10500).
  const lists: Partial<Options> = options.state.kind === 'ready' ? options.state.data : {};
  const isService = read('type') === 'Service';
  // The country the format rules dispatch on: the record's own where it carries one, US otherwise (piece 2).
  const country = read('countryCode');
  const countryCode = typeof country === 'string' && country ? country.toUpperCase() : 'US';
  /**
   * 🔒 A field another depends on empties that one when it changes (decision
   * 0737): changing a Location's state resets its County, because a county
   * belongs to one state.
   */
  const change = (key: string, value: unknown) => {
    const before = read(key);
    onChange(key, value);
    if (before === value) return;
    for (const dependent of EDIT_FIELDS[recordType].filter((field) => field.dependsOn === key)) onChange(dependent.key, null);
    // 🔒 A held choice whose option this change hides returns to the field's default, so a Deal turned from Service never sends the request route (decision 1912).
    const after = (field: string) => (field === key ? value : read(field));
    for (const choice of EDIT_FIELDS[recordType].filter((field) => field.options?.some((option) => option.shownWhen?.key === key))) {
      const held = read(choice.key);
      if (held !== undefined && held !== null && held !== '' && !shownOptions(choice, after, mode).some((option) => option.value === held)) onChange(choice.key, choice.defaultValue ?? null);
    }
  };
  const drawn = (field: EditField) =>
    field.optionsFrom === 'siteTypes'
      // 🔒 Decision 10500 (T3576): the Dropdown ending in Add New Site Type, for a holder of the list's write; a retired type the site keeps reads by its name.
      ? <SiteTypeField label={field.label} value={typeof read(field.key) === 'string' ? (read(field.key) as string) : null} valueLabel={typeof read(`${field.key}Label`) === 'string' ? (read(`${field.key}Label`) as string) : null} onChange={(value) => change(field.key, value)} apiBaseUrl={apiBaseUrl} dataAttribute={{ 'data-record-field': field.key }} />
      : field.kind === 'site'
      ? <SiteField field={field} value={read(field.key)} onChange={(value) => change(field.key, value)} apiBaseUrl={apiBaseUrl} companyId={recordType === 'CompanyRecord' ? recordId : null} companyName={recordLabel} />
      : field.kind === 'sealed'
        ? <SealedField field={field} mode={mode} onFile={read(`${field.key}OnFile`) === true} onVendorTile={read(`${field.key}OnVendorTile`) === true} value={read(field.key)} onChange={(value) => change(field.key, value)} />
      : field.kind === 'county'
        ? <CountyField field={field} value={read(field.key)} stateCode={field.dependsOn ? read(field.dependsOn) : null} countryCode={countryCode} onChange={(value) => change(field.key, value)} apiBaseUrl={apiBaseUrl} />
        : <Field field={field} value={shownValue(recordType, mode, field, read, lists)} valueLabel={read(`${field.key}Label`)} onChange={(value) => change(field.key, value)} options={field.optionsFrom ? (lists[field.optionsFrom] ?? []) : shownOptions(field, read, mode)} apiBaseUrl={apiBaseUrl} countryCode={countryCode} />;
  const shown = visibleEditFields(recordType, mode, read)
    .filter((field) => !(field.goodsOnly && isService))
    .filter((field) => only === null || only.includes(field.key))
    .concat(extra);
  return (
    <>
      {checkboxRuns(shown).map((run) =>
        run.length === 1 ? (
          <FieldGroup key={run[0]!.key} heading={run[0]!.heading ?? null}>
            {drawn(run[0]!)}
          </FieldGroup>
        ) : (
          <FieldGroup key={run[0]!.key} heading={run[0]!.heading ?? null}>
            {/* 🔒 Checkboxes side by side keep their own gap, so each box reads with its own word (decision 1451). */}
            <div className={styles.checkGroup} data-record-check-group={run.map((field) => field.key).join(' ')}>
              {run.map((field) => <Fragment key={field.key}>{drawn(field)}</Fragment>)}
            </div>
          </FieldGroup>
        ),
      )}
    </>
  );
}

/**
 * The fields in order, with each run of adjacent checkboxes gathered into one
 * group (decision 1451). A checkbox opening a heading of its own starts a new
 * run, so a group never straddles a heading.
 */
function checkboxRuns(fields: readonly EditField[]): EditField[][] {
  const runs: EditField[][] = [];
  for (const field of fields) {
    const last = runs[runs.length - 1];
    if (field.kind === 'boolean' && !field.heading && last && last[0]!.kind === 'boolean') last.push(field);
    else runs.push([field]);
  }
  return runs;
}

/**
 * 🔒 A sealed number, an EIN or a DUNS number (decision 3082): a create types
 * it; an edit reads On file or Not recorded beside Change, which opens the box
 * for a new number. 🔨 The held number never reaches the page, so the box
 * always opens empty and nothing typed is ever filled back in.
 * 🔒 Decision 7890: a vendor's Tax ID is changed on its Vendor tile, so the edit names that tile and draws no Change.
 */
function SealedField({ field, mode, onFile, onVendorTile, value, onChange }: { field: EditField; mode: 'create' | 'edit'; onFile: boolean; onVendorTile: boolean; value: unknown; onChange: (value: unknown) => void }) {
  const [changing, setChanging] = useState(false);
  const typed = typeof value === 'string' ? value : '';
  if (mode === 'edit' && onVendorTile) {
    return (
      <div className={styles.field} data-record-field={field.key} data-sealed-field="vendor-tile">
        <span className={fieldStyles.label}>{field.label}</span>
        <p className={styles.sealedHeld}>{onFile ? t('records.sealed.onFile') : t('records.sealed.notRecorded')}</p>
        <p className={styles.sealedHeld}>{t('records.sealed.onVendorTile')}</p>
      </div>
    );
  }
  if (mode === 'create' || changing) {
    return <TextInput size={Size.Medium} label={field.label} value={typed} onChange={onChange} name={field.key} autoComplete="off" />;
  }
  return (
    <div className={styles.field} data-record-field={field.key} data-sealed-field={onFile ? 'on-file' : 'not-recorded'}>
      <span className={fieldStyles.label}>{field.label}</span>
      <p className={styles.sealedHeld}>{onFile ? t('records.sealed.onFile') : t('records.sealed.notRecorded')}</p>
      <Button size={Size.Small} text={[t('records.sealed.change')]} actions={[() => setChanging(true)]} />
    </div>
  );
}

/**
 * 🔒 A Location's County is a pick from the Census Bureau's counties for the
 * location's state (decision 0737), never a word typed free. Before a United
 * States state is named the pick waits for one; a place outside the United
 * States types its county, because there is no list to hold it to.
 */
function CountyField({ field, value, stateCode, countryCode, onChange, apiBaseUrl }: { field: EditField; value: unknown; stateCode: unknown; countryCode: string; onChange: (value: unknown) => void; apiBaseUrl: string }) {
  const state = typeof stateCode === 'string' ? stateCode.trim().toUpperCase() : '';
  const asks = countryCode === 'US' && /^[A-Z]{2}$/.test(state);
  const read = useApi<{ stateCode: string; counties: string[] }>(asks ? countiesPath(state) : null, apiBaseUrl);
  const held = typeof value === 'string' ? value : '';
  if (countryCode !== 'US') return <TextInput size={Size.Medium} label={field.label} value={held} onChange={onChange} name={field.key} />;
  // 🔨 An answer missing its list reads as no counties, never a crash of the whole form.
  const counties = read.state.kind === 'ready' && Array.isArray(read.state.data?.counties) ? read.state.data.counties : [];
  // A county stored before the list existed still reads as itself until somebody picks one from the list.
  const options = held && !counties.includes(held) ? [held, ...counties] : counties;
  return (
    <div className={styles.field} data-record-field={field.key} data-county-state={state}>
      <Select
        size={Size.Medium}
        label={field.label}
        value={held}
        onChange={(chosen) => onChange(chosen || null)}
        placeholder={asks ? t('edit.none') : t('location.county.stateFirst')}
        options={options.map((name) => ({ value: name, label: name }))}
        disabled={!asks}
        dataAttribute={{ 'data-county-picker': 'true' }}
      />
    </div>
  );
}

/**
 * 🔒 Decision 0742: a new Item left with no tax category shows the category its
 * kind takes — Goods for a Good, repair labour for a Service — because that is
 * the one the save writes when none is named. The draft is not written, so the
 * server's own default stays the one answer and a changed kind moves the shown one.
 */
function shownValue(recordType: PagedRecord, mode: 'create' | 'edit', field: EditField, read: (key: string) => unknown, lists: Partial<Options>): unknown {
  const value = read(field.key);
  // 🔒 A choice whose start follows another field's (a Deal's Origin following its Type) draws that start until a choice is made (decision 10740).
  if (mode === 'create' && field.defaultFrom && (value === undefined || value === null || value === '')) return fieldDefault(field, read);
  if (recordType !== 'Item' || mode !== 'create' || field.key !== 'taxCategoryId' || (value !== undefined && value !== null && value !== '')) return value;
  const type = read('type') === 'Service' ? 'Service' : 'Good';
  return (lists.taxCategories ?? []).find((option) => option.label === TAX_CATEGORY_DEFAULT_BY_ITEM_TYPE[type])?.value ?? value;
}

interface FieldProps {
  field: EditField;
  value: unknown;
  onChange: (value: unknown) => void;
  /**
   * What the held id is called. 🔨 A field that stores an id and shows a name
   * needs the name for an id it did not choose in this session. 💰 The Account
   * on an existing Deal read "Chosen", because the Dropdown only ever learned a
   * name by being picked from (product owner, 2026-09-14).
   */
  valueLabel?: unknown;
  options: readonly { value: string; label: string; hint?: string | null }[];
  apiBaseUrl: string;
  countryCode: string;
  /** Told the name a company pick reads as, for a caller that names the choice back, as Edit Selected's confirmation does (decision 2890). */
  onLabel?: (label: string) => void;
}

/** One field as the record's form draws it; Edit Selected draws an Item's fields through it, so a bulk edit offers no picker the single edit does not (decision 2890). */
export function Field({ field, value, valueLabel, onChange, options, apiBaseUrl, countryCode, onLabel }: FieldProps) {
  if (field.kind === 'boolean') return <Checkbox label={field.label} checked={value === true} onChange={(checked) => onChange(checked)} />;
  if (field.kind === 'select') {
    // 🔒 A held choice the list no longer offers keeps its name in the field (decision 10100): a site carrying a retired type reads it chosen, and the save leaves it.
    const held = String(value ?? '');
    const kept = held !== '' && typeof valueLabel === 'string' && valueLabel !== '' && !options.some((option) => option.value === held) ? [{ value: held, label: valueLabel }] : [];
    const shown = [...options.map((option) => ({ value: option.value, label: option.label })), ...kept];
    return (
      // 🔒 Decision 0742: a choice carrying a one-line hint draws the chosen one's hint under the field, as the tax category does.
      <Select size={Size.Medium} label={field.label} value={held} onChange={(chosen) => onChange(chosen || null)} required={field.required ?? false} placeholder={t('edit.none')} options={shown} hint={options.find((option) => option.value === held)?.hint ?? null} dataAttribute={{ 'data-record-field': field.key }} />
    );
  }
  if (field.kind === 'radio') {
    return (
      <fieldset className={styles.radioGroup} data-record-field={field.key}>
        {/* 🔒 A required choice draws the required mark every other required field draws (T926 item 1). */}
        <legend className={styles.fieldLabel}>
          {field.label}
          {field.required ? <span className={fieldStyles.required} aria-hidden="true">{t('field.requiredMark')}</span> : null}
        </legend>
        {options.map((option) => (
          <label key={option.value} className={styles.radio}>
            <input type="radio" name={field.key} value={option.value} checked={value === option.value || ((value === undefined || value === null || value === '') && option.value === field.defaultValue)} onChange={() => onChange(option.value)} />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }
  if (field.kind === 'company') return <CompanyField field={field} value={value} valueLabel={valueLabel} onChange={onChange} apiBaseUrl={apiBaseUrl} {...(onLabel ? { onLabel } : {})} />;
  // 🔨 A date field is the Date Picker, never a text box the person types a date into (product owner, 2026-09-10).
  if (field.kind === 'number') {
    // The record stores dollars and the Numeric Input's currency mode stores cents, so the two convert at this seam and nowhere else (decision 0297).
    const numeric = field.numeric ?? 'number';
    const held = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
    const shown = held === null ? null : numeric === 'currency' ? dollarsToCents(held) : held;
    return (
      <NumericInput size={Size.Small}
        label={field.label}
        kind={numeric}
        value={shown}
        onChange={(next) => onChange(next === null ? null : numeric === 'currency' ? next / 100 : next)}
        required={field.required ?? false}
        name={field.key}
        {...(field.chars ? { chars: field.chars } : {})}
        {...(field.decimals === undefined ? {} : { decimals: field.decimals })}
      />
    );
  }
  if (field.kind === 'date') return <DatePicker size={Size.Small} label={field.label} value={String(value ?? '')} onChange={onChange} mode="date" required={field.required ?? false} name={field.key} />;
  // The input rules (decision 0290): a name title-cased and an email lower-cased on blur, a phone through the mask, a postal code's shape checked.
  const text0 = String(value ?? '');
  if (field.kind === 'name') return <TextInput size={Size.Medium} label={field.label} value={text0} onChange={onChange} onBlur={() => { const next = titleCaseName(text0); if (next !== text0) onChange(next); }} required={field.required ?? false} name={field.key} />;
  if (field.kind === 'email') {
    return (
      <TextInput size={Size.Medium}
        label={field.label}
        type="email"
        value={text0}
        onChange={onChange}
        onBlur={() => { const next = normalizeEmail(text0); if (next !== text0) onChange(next); }}
        validate={(typed) => { if (!typed.trim()) return null; const outcome = validateEmail(typed, countryCode); return outcome.ok ? null : outcome.message; }}
        required={field.required ?? false}
        name={field.key}
      />
    );
  }
  // A phone is the Phone Input (decision 0340): the stored E.164 value reopens in its parts, and the country opens on the record's own.
  if (field.kind === 'phone') return <PhoneInput size={Size.Medium} label={field.label} value={text0} onChange={onChange} defaultCountry={countryCode} required={field.required ?? false} name={field.key} />;
  if (field.kind === 'postalCode') {
    return (
      <TextInput size={Size.Medium}
        label={field.label}
        value={text0}
        onChange={onChange}
        onBlur={() => { const next = normalizePostalCode(text0, countryCode); if (next !== text0) onChange(next); }}
        validate={(typed) => { if (!typed.trim()) return null; const outcome = validatePostalCode(typed, countryCode); return outcome.ok ? null : outcome.message; }}
        required={field.required ?? false}
        name={field.key}
      />
    );
  }
  // 🔒 A paragraph is piece 3's Textarea, two lines tall and growing, its line breaks kept (T87; decision 0685).
  if (field.kind === 'multiline') return <Textarea size={Size.Large} label={field.label} value={text0} onChange={onChange} required={field.required ?? false} name={field.key} />;
  // 🔒 An address is lines, every one drawn and every one kept (T3078; decision 7400): Address, Address line 2 and any further line the record holds.
  if (field.kind === 'lines') return <AddressLinesField field={field} value={value} onChange={onChange} />;
  return <TextInput size={Size.Medium} label={field.label} value={text0} onChange={onChange} required={field.required ?? false} name={field.key} />;
}

/**
 * An address's lines as one input each (T3078; decision 7400). The first reads
 * as the field's own label, the second as Address line 2, and a record holding
 * more lines draws each of them too, so Save Changes never writes fewer lines
 * than the record had. 🔨 A change writes the whole array, never one line.
 */
function AddressLinesField({ field, value, onChange }: { field: EditField; value: unknown; onChange: (value: unknown) => void }) {
  const held = Array.isArray(value) ? value.map((line) => String(line ?? '')) : typeof value === 'string' && value ? [value] : [];
  const drawn = Math.max(2, held.length);
  const labelOf = (index: number) => (index === 0 ? field.label : index === 1 ? t('edit.addressLine2') : fill(t('edit.addressLineN'), { n: String(index + 1) }));
  return (
    <>
      {Array.from({ length: drawn }, (_, index) => (
        <TextInput
          key={index}
          size={Size.Medium}
          label={labelOf(index)}
          value={held[index] ?? ''}
          onChange={(next) => onChange(withoutTrailingBlanks(Array.from({ length: drawn }, (_unused, at) => (at === index ? next : (held[at] ?? '')))))}
          required={index === 0 && (field.required ?? false)}
          name={index === 0 ? field.key : `${field.key}${index + 1}`}
        />
      ))}
    </>
  );
}

/** The lines as typed, a blank box after the last line left unsaid, so an untouched second box is not a change. */
function withoutTrailingBlanks(lines: string[]): string[] {
  let end = lines.length;
  while (end > 0 && lines[end - 1]!.trim() === '') end -= 1;
  return lines.slice(0, end);
}

/** The manufacturer is a Company: a Dropdown whose Add New row opens the Company quick-create, the pattern a Deal uses (piece 17). */
function CompanyField({ field, value, valueLabel, onChange, apiBaseUrl, onLabel }: Omit<FieldProps, 'options' | 'countryCode'>) {
  // 🔒 The field's own side, where it names one (T184; decision 0637): a Deal's or a Quote's Account offers customers alone.
  const search = useCompanySearch(apiBaseUrl, field.counterparty ?? null);
  const [picked, setPicked] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const chosen = typeof value === 'string' && value ? value : null;
  // The name picked in this session, else the one the answer carries for the held id.
  const held = typeof valueLabel === 'string' && valueLabel.trim() ? valueLabel : null;
  const name = picked ?? held;
  return (
    <div className={styles.field} data-record-field={field.key}>
      <Dropdown size={Size.Medium} label={field.label} required={field.required ?? false} entityLabel={t('edit.companyEntity')} search={search} value={name ?? (chosen ? t('edit.companyChosen') : null)} onSelect={(row) => { setPicked(row.line1); onLabel?.(row.line1); onChange(row.id); }} onAddNew={() => setCreating(true)} />
      <NewRecordModal open={creating} recordType="CompanyRecord" apiBaseUrl={apiBaseUrl} {...(field.counterparty ? { initialValues: { [field.counterparty === 'customer' ? 'isCustomer' : 'isVendor']: true } } : {})} onCancel={() => setCreating(false)} onCreated={(created) => { setPicked(created.label); onLabel?.(created.label); onChange(created.id); }} />
    </div>
  );
}

/** A field with the small heading that opens a group above it, where the register names one (decision 0669). */
function FieldGroup({ heading, children }: { heading: string | null; children: ReactNode }) {
  if (!heading) return <>{children}</>;
  return (
    <>
      <h3 className={styles.fieldGroupHeading} data-record-field-group={heading}>{heading}</h3>
      {children}
    </>
  );
}

/**
 * 🔒 The Company's default billing site (decision 0669): the Site picker over the
 * company's open sites, ending in the Add New Site row, which opens the one + Add
 * Site window with the company locked. Clear writes none.
 */
function SiteField({ field, value, onChange, apiBaseUrl, companyId, companyName }: { field: EditField; value: unknown; onChange: (value: unknown) => void; apiBaseUrl: string; companyId: string | null; companyName: string | null }) {
  const path = companyId ? companyBillingSitesPath(companyId) : null;
  const read = useApi<{ companyId: string; sites: SiteChoiceRow[] }>(path, apiBaseUrl);
  // 🔨 An answer missing its list reads as no sites, never a crash of the whole form.
  const answered = read.state.kind === 'ready' && companyId ? { companyId, sites: Array.isArray(read.state.data?.sites) ? read.state.data.sites : [] } : null;
  const choices = useSiteChoices(answered, path, apiBaseUrl);
  const chosen = typeof value === 'string' && value ? value : null;
  return (
    <div className={styles.field} data-record-field={field.key}>
      <SitePicker picker="billTo" label={field.label} value={chosen} onChange={(siteId) => onChange(siteId)} sites={choices.sites} companyId={companyId} companyName={companyName} apiBaseUrl={apiBaseUrl} placeholder={t('edit.none')} onCreated={choices.added((siteId) => onChange(siteId))} />
      {chosen ? <Button size={Size.Small} text={[t('edit.clearSite')]} actions={[() => onChange(null)]} /> : null}
    </div>
  );
}

